import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Seo from "@/components/Seo";

const SERVICES = [
    {
        key: "sprint",
        eyebrow: "Starting point",
        name: "AI Opportunity Sprint",
        purpose: "A structured starting point that identifies where AI creates the greatest impact inside your business and defines a clear rollout plan. The outcome is clarity, not experimentation.",
        deliverables: [
            "Business workflow assessment",
            "AI opportunity map",
            "Prioritised use cases",
            "Implementation roadmap",
            "Impact framing",
        ],
        engagement: "Structured engagement",
        scope: "Defined scope based on your business",
        cta: "Discuss the Sprint",
    },
    {
        key: "cao",
        eyebrow: "Ongoing leadership",
        name: "Fractional Chief AI Officer",
        purpose: "Ownership of AI inside your business. Prioritisation, implementation and ongoing optimisation run by a dedicated executive partner, without the cost of a full-time hire.",
        deliverables: [
            "Monthly AI roadmap ownership",
            "Weekly working cadence",
            "Department-level opportunity mapping",
            "Implementation oversight",
            "Design and rollout of AI-powered workflows",
            "Practical system implementation (not just planning)",
            "Team enablement",
            "Executive reporting",
        ],
        engagement: "Ongoing engagement",
        scope: "Defined scope based on your business",
        cta: "Book a consultation",
    },
    {
        key: "team",
        eyebrow: "Expanded delivery",
        name: "Fractional AI Team",
        purpose: "A broader delivery model for organisations needing faster rollout or multi-department transformation. Leadership, implementation and adoption managed end to end.",
        deliverables: [
            "AI leadership",
            "Implementation coordination",
            "Build and deployment of AI-powered operational systems",
            "Development of reusable automation workflows",
            "Training and adoption support",
            "Contractor delivery resources",
            "Monthly reporting",
            "Ongoing optimisation and expansion of AI systems",
        ],
        engagement: "Ongoing engagement",
        scope: "Defined scope based on your business",
        cta: "Talk to us",
    },
];

export default function Services() {
    return (
        <div data-testid="page-services">
            <Seo
                title="AI Leadership Services | Chief AI Partners"
                description="Execution, not experimentation. Chief AI Partners takes ownership of AI inside mid-market organisations through structured engagements."
                path="/services"
            />

            <section className="cap-section pt-24 md:pt-32 pb-16" data-testid="services-hero">
                <div className="cap-container grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-8">
                        <div className="cap-eyebrow mb-6">Services</div>
                        <h1 className="font-serif text-navy text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
                            Execution, not experimentation.
                        </h1>
                    </div>
                    <div className="lg:col-span-4 lg:pt-4">
                        <p className="text-slate text-lg leading-relaxed">
                            Engagements are structured based on where you are today and how quickly you need to move.
                        </p>
                    </div>
                </div>
                <div className="cap-container mt-12"><div className="cap-rule" /></div>
            </section>

            {SERVICES.map((s, idx) => (
                <section
                    key={s.key}
                    className={`cap-section ${idx % 2 === 1 ? "bg-sand/40 border-y border-sand" : ""}`}
                    data-testid={`service-section-${s.key}`}
                >
                    <div className="cap-container grid grid-cols-1 lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-5">
                            <div className="cap-eyebrow mb-4">{s.eyebrow}</div>
                            <h2 className="font-serif text-navy text-4xl md:text-5xl leading-[1.05] tracking-tight">
                                {s.name}
                            </h2>
                            <p className="mt-6 text-slate text-lg leading-relaxed">{s.purpose}</p>

                            <div className="mt-8 space-y-2 text-sm">
                                <div className="flex flex-wrap items-baseline gap-2">
                                    <span className="text-xs uppercase tracking-[0.18em] text-teal font-semibold">Engagement</span>
                                    <span className="text-navy font-medium">{s.engagement}</span>
                                </div>
                                <div className="flex flex-wrap items-baseline gap-2">
                                    <span className="text-xs uppercase tracking-[0.18em] text-teal font-semibold">Scope</span>
                                    <span className="text-navy font-medium">{s.scope}</span>
                                </div>
                            </div>

                            <Link
                                to="/contact"
                                className="cap-btn-primary mt-8"
                                data-testid={`service-cta-${s.key}`}
                            >
                                {s.cta} <ArrowRight size={16} />
                            </Link>
                        </div>

                        <div className="lg:col-span-7">
                            <div className="cap-eyebrow mb-5 text-slate">Inside the engagement</div>
                            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                                {s.deliverables.map((d) => (
                                    <li key={d} className="flex items-start gap-3 text-navy text-base">
                                        <Check className="mt-1 text-teal shrink-0" size={16} strokeWidth={2.2} />
                                        <span>{d}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>
            ))}

            <section className="cap-section bg-navy text-offwhite" data-testid="services-final-cta">
                <div className="cap-container text-center max-w-3xl mx-auto">
                    <div className="cap-eyebrow mb-4" style={{ color: "#2EA6A1" }}>Next step</div>
                    <h2 className="font-serif text-offwhite text-4xl md:text-5xl leading-[1.05] tracking-tight">
                        Not sure which engagement fits your business?
                    </h2>
                    <p className="mt-6 text-offwhite/80 text-lg leading-relaxed">
                        Book a 15-minute conversation. We will help you decide whether to start with a Sprint, a fractional Chief AI Officer or a broader team.
                    </p>
                    <Link
                        to="/contact"
                        className="inline-flex items-center justify-center gap-2 bg-offwhite text-navy font-semibold px-6 py-3 mt-8 border border-offwhite hover:bg-sand transition-colors"
                        data-testid="services-book-cta"
                    >
                        Book a 15-minute conversation <ArrowRight size={16} />
                    </Link>
                </div>
            </section>
        </div>
    );
}
